import React, { memo, useState, useEffect, useRef } from "react"
import { Link, useStaticQuery, graphql } from "gatsby"
import logoWhite from "../images/logo/logo_white_LST_wo_tag.png"
import KnowUs from "./KnowUs"
import HowWeHelp from "./HowWeHelp"
import OurPrimaryFocus from "./OurPrimaryFocus"
import InformationHub from "./InformationHub"
import "../styles/header.scss"

const NAV_ITEMS = [
  { key: "know-us",           label: "Who We Are" },
  { key: "How-We-Help",       label: "How We Help" },
  { key: "our-primary-focus", label: "Our Primary Focus" },
  { key: "information-hub",   label: "Information Hub" },
]

const Chevron = ({ open }) => (
  <svg
    width="11" height="11" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

const Header = () => {
  const [activeTab, setActiveTab]   = useState(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const wrapRef = useRef(null)

  const queryData = useStaticQuery(graphql`
    query HeaderQuery {
      # The header wordmark is a 4269x767 PNG painted into a 154x28 slot. Held
      # as a webpack import it shipped at full size to every page and cost the
      # browser a 13MB decode to draw a 154px logo. Capped here at 320px, which
      # covers 2x on the largest slot the header uses.
      headerLogo: file(relativePath: { eq: "logo/logo_white_LST_wo_tag.png" }) {
        childImageSharp {
          fluid(maxWidth: 320, quality: 90) {
            src
            srcSet
            sizes
            presentationWidth
            presentationHeight
          }
        }
      }
      VMLogoWhite: file(relativePath: { eq: "logo_white_LST.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      VMLogoBlack: file(relativePath: { eq: "logo_black_LST.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      UMTVisiomate: file(relativePath: { eq: "lifeatvisiomate/UMT_Visiomate3.jpg" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      umrahshuttleapp: file(relativePath: { eq: "mobile/umrahshuttleapp.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      perimflowForMenu: file(relativePath: { eq: "news/perimflowop2.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      learningcircle: file(relativePath: { eq: "news/learning_circle.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      instagram: file(relativePath: { eq: "instagram.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      facebook: file(relativePath: { eq: "facebook.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      linkedin: file(relativePath: { eq: "linkedin.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      twitter: file(relativePath: { eq: "twitter_bird.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      youtube: file(relativePath: { eq: "youtube.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
    }
  `)

  const toggleTab = key => setActiveTab(prev => prev === key ? null : key)

  useEffect(() => {
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setActiveTab(null)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  return (
    <header ref={wrapRef} className="ant-layout-header site-header">
      {/* ── Top bar ── */}
      <div className="site-header__bar">

        <Link to="/" className="site-header__logo">
          {queryData.headerLogo ? (
            <img
              src={queryData.headerLogo.childImageSharp.fluid.src}
              srcSet={queryData.headerLogo.childImageSharp.fluid.srcSet}
              sizes={queryData.headerLogo.childImageSharp.fluid.sizes}
              width={queryData.headerLogo.childImageSharp.fluid.presentationWidth}
              height={queryData.headerLogo.childImageSharp.fluid.presentationHeight}
              alt="Visiomate"
            />
          ) : (
            <img src={logoWhite} alt="Visiomate" />
          )}
        </Link>

        <nav className="site-header__nav">
          {NAV_ITEMS.map(({ key, label }) => (
            <button
              key={key}
              className={`site-header__nav-btn${activeTab === key ? " is-active" : ""}`}
              onClick={() => toggleTab(key)}
              // The panel each button controls is now permanently in the DOM,
              // so the button's state has to be announced rather than inferred
              // from whether anything appeared.
              aria-expanded={activeTab === key}
            >
              {label} <Chevron open={activeTab === key} />
            </button>
          ))}
          {/* A REAL ANCHOR, DELIBERATELY OUTSIDE THE DROPDOWNS. /offers is the
              catalogue of everything we sell and had no sitewide chrome link at
              all — not in the header, and the footer's "Offers" column heading
              was plain text (NAVIGATION-AUDIT §2). Every other link in this bar
              lives inside a panel that only renders after a click, so it is
              absent from the server-rendered HTML. This one ships in the
              markup, which is the whole point of putting it here. */}
          <Link to="/offers" className="site-header__nav-btn">
            Offers
          </Link>
        </nav>

        <div className="site-header__cta">
          <Link to="/career" className="site-header__btn site-header__btn--filled">
            Explore Careers
          </Link>
          <Link to="/contact" className="site-header__btn site-header__btn--outline">
            Let's Talk Business
          </Link>
        </div>

        <button
          className="site-header__hamburger"
          onClick={() => setMobileOpen(o => !o)}
          aria-label="Toggle menu"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            {mobileOpen
              ? <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
              : <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>
            }
          </svg>
        </button>
      </div>

      {/* ── Desktop dropdown panel — ALWAYS IN THE MARKUP ──
           Was `{activeTab && <div>…}`, which mounted the panel only after a
           click. The consequence was that the header shipped three anchors and
           the site's entire information architecture — roughly thirty links —
           existed nowhere in the HTML, so nothing reading the markup could see
           it (RENDERING-AND-PAYLOAD-AUDIT §2b).

           All four panels now render on every page and CSS decides which is
           visible: the wrapper carries the open/closed state, and
           `.site-header__panel` shows exactly one child. The 0.18s
           translateY(-6px) → 0 fade that `hdr-drop-in` used to run on mount is
           now the wrapper's transition, so the open still looks and feels the
           same.

           THE MOBILE PANEL BELOW STAYS CONDITIONAL ON PURPOSE. It renders these
           same four components, so mounting it here as well would put a second
           copy of all thirty links in every document — duplicate internal links
           and double the header markup, which is a worse problem than the one
           being fixed. The desktop set is what a crawler reads; the mobile panel
           is reachable only by tapping the hamburger, which needs JS anyway. */}
      <div
        className={`site-header__dropdown${activeTab ? " is-open" : ""}`}
        aria-hidden={activeTab ? undefined : "true"}
      >
        <div className={`site-header__panel${activeTab === "know-us" ? " is-active" : ""}`}>
          <KnowUs />
        </div>
        <div className={`site-header__panel${activeTab === "How-We-Help" ? " is-active" : ""}`}>
          <HowWeHelp />
        </div>
        <div className={`site-header__panel${activeTab === "our-primary-focus" ? " is-active" : ""}`}>
          <OurPrimaryFocus queryData={queryData} />
        </div>
        <div className={`site-header__panel${activeTab === "information-hub" ? " is-active" : ""}`}>
          <InformationHub queryData={queryData} />
        </div>
      </div>

      {/* ── Mobile nav panel ── */}
      {mobileOpen && (
        <div className="site-header__mobile-panel">
          {NAV_ITEMS.map(({ key, label }) => (
            <div key={key} className="site-header__mobile-group">
              <button
                className={`site-header__mobile-item${activeTab === key ? " is-active" : ""}`}
                onClick={() => toggleTab(key)}
              >
                {label} <Chevron open={activeTab === key} />
              </button>
              {activeTab === key && (
                <div className="site-header__mobile-content">
                  {key === "know-us"           && <KnowUs />}
                  {key === "How-We-Help"       && <HowWeHelp />}
                  {key === "our-primary-focus" && <OurPrimaryFocus queryData={queryData} />}
                  {key === "information-hub"   && <InformationHub queryData={queryData} />}
                </div>
              )}
            </div>
          ))}
          <div className="site-header__mobile-group">
            <Link to="/offers" className="site-header__mobile-item">
              Offers
            </Link>
          </div>
          <div className="site-header__mobile-cta">
            <Link to="/career" className="site-header__btn site-header__btn--filled">Explore Careers</Link>
            <Link to="/contact" className="site-header__btn site-header__btn--outline">Let's Talk Business</Link>
          </div>
        </div>
      )}

    </header>
  )
}

export default memo(Header)
